import { useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, Lightformer } from "@react-three/drei";
import { ArrowLeft, Check } from "lucide-react";
import LogoMark3D from "./three/LogoMark3D";

const SpinningMark = () => {
  const ref = useRef();
  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.9;
  });
  return (
    <group ref={ref}>
      <LogoMark3D scale={1.3} />
    </group>
  );
};

const Burst = () => {
  const particles = useMemo(
    () =>
      Array.from({ length: 42 }, (_, i) => {
        const angle = (i / 42) * Math.PI * 2 + Math.random() * 0.4;
        const dist = 140 + Math.random() * 300;
        return {
          x: Math.cos(angle) * dist,
          y: Math.sin(angle) * dist - 60,
          size: 5 + Math.random() * 7,
          round: Math.random() > 0.5,
          delay: Math.random() * 0.15,
          duration: 1.1 + Math.random() * 0.9,
          opacity: 0.6 + Math.random() * 0.4,
        };
      }),
    []
  );

  if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
      {particles.map((p, i) => (
        <span
          key={i}
          className={p.round ? "rounded-full" : "rotate-45"}
          style={{
            position: "absolute",
            width: p.size,
            height: p.size,
            background: `rgba(255,255,255,${p.opacity})`,
            boxShadow: "0 0 6px rgba(255,255,255,0.5)",
            opacity: 0,
            "--tx": `${p.x}px`,
            "--ty": `${p.y}px`,
            animation: `burst-fly ${p.duration}s cubic-bezier(0.15, 0.85, 0.45, 1) ${0.35 + p.delay}s both`,
          }}
        />
      ))}
    </div>
  );
};

const Congratulations = ({ onHome }) => (
  <motion.div
    data-testid="congratulations-page"
    initial={{ opacity: 0, filter: "blur(16px)" }}
    animate={{ opacity: 1, filter: "blur(0px)" }}
    exit={{ opacity: 0, filter: "blur(16px)" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-[#050505]/95 backdrop-blur-2xl px-6"
  >
    <Burst />
    <motion.div
      className="h-[170px] w-[170px] sm:h-[210px] sm:w-[210px]"
      initial={{ opacity: 0, scale: 0.75 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    >
      <Canvas dpr={[1, 1.5]} camera={{ position: [0, 0, 7], fov: 42 }} gl={{ antialias: true, alpha: true }}>
        <SpinningMark />
        <ambientLight intensity={0.3} />
        <spotLight position={[5, 6, 6]} angle={0.5} penumbra={1} intensity={160} color="#ffffff" />
        <Environment resolution={256} frames={1}>
          <Lightformer intensity={3.5} position={[0, 5, -6]} scale={[12, 4, 1]} color="#ffffff" />
          <Lightformer intensity={2.2} position={[-6, 2, 2]} rotation-y={Math.PI / 2} scale={[8, 2.5, 1]} color="#e8e8e8" />
          <Lightformer intensity={2.2} position={[6, -1, 2]} rotation-y={-Math.PI / 2} scale={[8, 2.5, 1]} color="#ffffff" />
        </Environment>
      </Canvas>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.45, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center text-center"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/25 bg-white/5" style={{ animation: "pulse-glow 2.4s ease-in-out infinite" }}>
        <Check size={24} className="text-white" strokeWidth={2} />
      </span>
      <p className="font-display mt-8 text-[11px] font-medium tracking-[0.5em] text-white/50 uppercase">
        Enquiry Launched
      </p>
      <h1 data-testid="congratulations-heading" className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter">
        <span className="chrome-text">Congratulations.</span>
      </h1>
      <p data-testid="congratulations-message" className="mt-6 max-w-md text-base md:text-lg font-light leading-relaxed text-white/60">
        Great job — you have taken the first step. We've received your enquiry and will contact
        you shortly via the contact information you have provided.
      </p>
      <button
        data-testid="back-to-home-button"
        onClick={onHome}
        className="group mt-10 inline-flex items-center gap-3 rounded-full bg-white px-9 py-4 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.04] active:scale-95"
      >
        <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
        Back to Home
      </button>
    </motion.div>
  </motion.div>
);

export default Congratulations;
